import { ALL_LOCATIONS_ID, ALL_DEALERSHIPS_ID } from '../util/locationUtils';
import { HTTPService } from './httpService';

const KEY_USER = 'userAPI';
const KEY_COMPANY = 'companyAPI';
const KEY_DEALER_GROUP = 'dealerGroupAPI';
const KEY_SCOPE = 'scopeAPI';
const KEY_STRIPE = 'stripeCapabilities';

const API = new HTTPService();

function readJSON(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* no-op */ }
}

function clearKey(key) {
  try { localStorage.removeItem(key); } catch { /* no-op */ }
}

const ScopeService = {

  // ------------------------------------------------------------------
  // User
  // ------------------------------------------------------------------

  getUser() {
    return readJSON(KEY_USER);
  },

  getUserId() {
    return readJSON(KEY_USER)?.User_id ?? null;
  },

  getUserRole() {
    return readJSON(KEY_USER)?.role ?? null;
  },

  // ------------------------------------------------------------------
  // Scope tree (full server response)
  // ------------------------------------------------------------------

  /**
   * Fetch the user's scope tree from /users/:userId/scope and cache it.
   * Does NOT activate a scope — caller picks an index and calls activateScopeByIndex.
   */
  async loadScopeTree(userId) {
    const { data } = await API.get(`/users/${userId}/scope`);
    const payload = data || {};
    const tree = {
      userId: payload.userId ?? userId,
      userType: payload.userType || 'installer',
      scopes: Array.isArray(payload.scopes) ? payload.scopes : [],
      defaultScopeIndex: Number.isInteger(payload.defaultScopeIndex) ? payload.defaultScopeIndex : 0,
      activeScopeIndex: null,
    };
    writeJSON(KEY_SCOPE, tree);
    return tree;
  },

  getScopeTree() {
    return readJSON(KEY_SCOPE);
  },

  getAllScopes() {
    return readJSON(KEY_SCOPE)?.scopes || [];
  },

  getActiveScopeIndex() {
    const tree = readJSON(KEY_SCOPE);
    if (!tree) return null;
    return tree.activeScopeIndex ?? tree.defaultScopeIndex ?? 0;
  },

  /**
   * Resolve & activate a scope by index. Fetches the entity record
   * (installer / dealer) so back-compat companyAPI is populated, then
   * writes companyAPI + dealerGroupAPI from the combined data.
   *
   * Returns the activated scope.
   */
  async activateScopeByIndex(index) {
    const tree = readJSON(KEY_SCOPE);
    if (!tree) throw new Error('ScopeService: no scope tree loaded');
    const scope = tree.scopes[index];
    if (!scope) throw new Error(`ScopeService: scope index ${index} out of range`);

    tree.activeScopeIndex = index;
    writeJSON(KEY_SCOPE, tree);

    await this._writeBackCompatRecords(scope);
    return scope;
  },

  /**
   * Find a scope by (type, id) and activate it. Returns the scope
   * (or null if not found).
   */
  async activateScopeByTypeId(type, id) {
    const scopes = this.getAllScopes();
    const idx = scopes.findIndex(s => s.type === type && String(s.id) === String(id));
    if (idx < 0) return null;
    return this.activateScopeByIndex(idx);
  },

  /**
   * Re-fetch and re-activate the current scope. Useful after the entity
   * record changes (e.g., user toggles time_tracking_mode in profile).
   */
  async refreshActiveScope() {
    const idx = this.getActiveScopeIndex();
    if (idx === null) return null;
    return this.activateScopeByIndex(idx);
  },

  /**
   * Internal: fetches the entity record for a scope and writes
   * companyAPI + dealerGroupAPI in the legacy shape so all existing
   * consumers (read via getCompany / direct localStorage) keep working.
   */
  async _writeBackCompatRecords(scope) {
    if (!scope) return;
    const type = scope.type;

    if (type === 'installer' || type === 'installer_location') {
      const installerId = type === 'installer' ? scope.id : scope.parentId;
      const { data } = await API.get(`/installers/${installerId}`);
      const installerRow = (Array.isArray(data) ? data[0] : data) || {};

      // Locations come from the scope response (already trimmed for sub-scoped users)
      const locations = type === 'installer'
        ? (scope.locations || [])
        : (scope.location ? [scope.location] : []);

      // For installer_location scope, the user is pinned to the single location.
      // For installer scope, default to the first location. ALL_LOCATIONS_ID kept
      // as null so existing code (which treats null as "all") keeps working.
      let selectedLocationId = null;
      if (type === 'installer_location') {
        selectedLocationId = scope.id;
      }

      const companyRecord = {
        ...installerRow,
        Installer_id: installerId,
        Installer_Name: installerRow.Installer_Name || installerRow.installer_name || scope.name,
        locations,
        selectedLocationId,
      };

      writeJSON(KEY_COMPANY, companyRecord);
      clearKey(KEY_DEALER_GROUP);
      return;
    }

    if (type === 'dealer') {
      const { data } = await API.get(`/dealers/${scope.id}`);
      const dealerRow = (Array.isArray(data) ? data[0] : data) || {};
      writeJSON(KEY_COMPANY, {
        ...dealerRow,
        dealer_id: scope.id,
        dealer_name: dealerRow.dealer_name || scope.name,
      });
      clearKey(KEY_DEALER_GROUP);
      return;
    }

    if (type === 'dealer_group') {
      // The group's "company" record is the first dealership (so dealer_address_zip etc. resolve).
      // selectedDealerId starts at the user's parent_id (sub-scope) or ALL_DEALERSHIPS_ID.
      const dealerships = scope.dealerships || [];
      const firstDealership = dealerships[0] || {};
      writeJSON(KEY_COMPANY, {
        ...firstDealership,
        dealer_id: firstDealership.dealer_id ?? null,
        dealer_name: firstDealership.dealer_name || scope.name,
      });
      writeJSON(KEY_DEALER_GROUP, {
        isDealerGroup: true,
        groupId: scope.id,
        groupName: scope.name,
        selectedDealerId: scope.parentId != null ? scope.parentId : ALL_DEALERSHIPS_ID,
        dealerships,
      });
    }
  },

  /**
   * Wipes scope-related localStorage (e.g., on sign-out).
   */
  clearAll() {
    [KEY_SCOPE, KEY_COMPANY, KEY_DEALER_GROUP, KEY_STRIPE].forEach(clearKey);
  },

  // ------------------------------------------------------------------
  // Active-scope derivations (read by everywhere downstream)
  // ------------------------------------------------------------------

  /**
   * Active scope object from the cached tree (preferred).
   * Falls back to a synthesized scope from companyAPI/dealerGroupAPI for code
   * paths that haven't migrated to scope-tree-aware reads yet.
   */
  getActiveScope() {
    const tree = readJSON(KEY_SCOPE);
    if (tree && Array.isArray(tree.scopes)) {
      const idx = tree.activeScopeIndex ?? tree.defaultScopeIndex ?? 0;
      const fromTree = tree.scopes[idx];
      if (fromTree) return fromTree;
    }
    // Legacy fallback — derive from companyAPI / dealerGroupAPI
    return this._legacyActiveScope();
  },

  _legacyActiveScope() {
    const userType = this.getUserType();
    const company = readJSON(KEY_COMPANY);
    const dgInfo = readJSON(KEY_DEALER_GROUP);

    if (userType === 'dealer_group') {
      return {
        type: 'dealer_group',
        id: dgInfo?.groupId ?? null,
        name: dgInfo?.groupName || company?.dealer_name || '',
        role: this.getUserRole(),
        parentId: null,
        scopedName: null,
        dealerships: dgInfo?.dealerships || [],
      };
    }
    if (userType === 'dealer') {
      return {
        type: 'dealer',
        id: company?.dealer_id ?? null,
        name: company?.dealer_name || '',
        role: this.getUserRole(),
        parentId: null,
        scopedName: null,
      };
    }
    return {
      type: 'installer',
      id: company?.Installer_id ?? null,
      name: company?.Installer_Name || '',
      role: this.getUserRole(),
      parentId: null,
      scopedName: null,
      locations: company?.locations || [],
    };
  },

  /**
   * Active scope's role (per-row from user_companies). Falls back to the
   * global user.role when the scope tree isn't loaded yet.
   */
  getActiveScopeRole() {
    return this.getActiveScope()?.role || this.getUserRole();
  },

  getUserType() {
    const tree = readJSON(KEY_SCOPE);
    if (tree?.userType) return tree.userType === 'dealer' && this.isDealerGroupUser()
      ? 'dealer_group'
      : tree.userType;
    // Legacy fallback
    const dgInfo = readJSON(KEY_DEALER_GROUP);
    if (dgInfo?.isDealerGroup) return 'dealer_group';
    const user = readJSON(KEY_USER);
    const raw = (user?.User_type || '').toLowerCase();
    if (raw === 'dealer') return 'dealer';
    return 'installer';
  },

  isDealerGroupUser() {
    return Boolean(readJSON(KEY_DEALER_GROUP)?.isDealerGroup);
  },

  // ------------------------------------------------------------------
  // Back-compat accessors (companyAPI / dealerGroupAPI)
  // ------------------------------------------------------------------

  getCompany() {
    return readJSON(KEY_COMPANY);
  },

  getCompanyName() {
    const scope = this.getActiveScope();
    if (scope?.name) return scope.name;
    const company = readJSON(KEY_COMPANY);
    if (this.isDealerGroupUser()) {
      return readJSON(KEY_DEALER_GROUP)?.groupName || company?.dealer_name || '';
    }
    return company?.Installer_Name || company?.dealer_name || '';
  },

  getEntityId() {
    const scope = this.getActiveScope();
    if (scope?.id != null) return scope.id;
    const company = readJSON(KEY_COMPANY);
    return company?.Installer_id ?? company?.dealer_id ?? null;
  },

  getLocations() {
    const scope = this.getActiveScope();
    if (scope?.type === 'installer') return scope.locations || [];
    if (scope?.type === 'installer_location') return scope.location ? [scope.location] : [];
    return readJSON(KEY_COMPANY)?.locations || [];
  },

  getSelectedLocationId() {
    const company = readJSON(KEY_COMPANY);
    const val = company?.selectedLocationId;
    return val === undefined || val === null ? ALL_LOCATIONS_ID : val;
  },

  setSelectedLocationId(locationId) {
    const company = readJSON(KEY_COMPANY);
    if (!company) return;
    company.selectedLocationId = locationId === ALL_LOCATIONS_ID ? null : locationId;
    writeJSON(KEY_COMPANY, company);
  },

  getDealerGroup() {
    return readJSON(KEY_DEALER_GROUP);
  },

  getGroupId() {
    return readJSON(KEY_DEALER_GROUP)?.groupId ?? null;
  },

  getGroupName() {
    return readJSON(KEY_DEALER_GROUP)?.groupName || '';
  },

  getSelectedDealerId() {
    const dgInfo = readJSON(KEY_DEALER_GROUP);
    if (!dgInfo) return ALL_DEALERSHIPS_ID;
    const val = dgInfo.selectedDealerId;
    return val === undefined || val === null ? ALL_DEALERSHIPS_ID : val;
  },

  setSelectedDealerId(dealerId) {
    const dgInfo = readJSON(KEY_DEALER_GROUP) || {};
    dgInfo.selectedDealerId = dealerId === ALL_DEALERSHIPS_ID ? ALL_DEALERSHIPS_ID : dealerId;
    writeJSON(KEY_DEALER_GROUP, dgInfo);
  },

  // ------------------------------------------------------------------
  // Multi-location rollups (capacity / calendar auth)
  // ------------------------------------------------------------------

  /**
   * Effective capacity for the active scope.
   * - installer_location: that location's capacity
   * - installer + a specific location selected: that location's capacity
   * - installer + "all locations": sum across locations (treats company as a fleet of bays)
   * - dealer / dealer_group: 0 (concept doesn't apply)
   */
  getEffectiveCapacity() {
    const scope = this.getActiveScope();
    if (!scope) return 0;
    if (scope.type === 'installer_location') {
      return Number(scope.location?.capacity) || 0;
    }
    if (scope.type === 'installer') {
      const locations = scope.locations || [];
      const selected = this.getSelectedLocationId();
      if (selected !== ALL_LOCATIONS_ID) {
        const loc = locations.find(l => String(l.location_id) === String(selected));
        return Number(loc?.capacity) || 0;
      }
      return locations.reduce((sum, l) => sum + (Number(l.capacity) || 0), 0);
    }
    return 0;
  },

  /**
   * True only when every location in scope has Google Calendar OAuth connected.
   * For dealer / dealer_group, returns true (concept doesn't apply).
   */
  isCalendarAuthenticated() {
    const scope = this.getActiveScope();
    if (!scope) return false;
    if (scope.type === 'installer_location') {
      return Number(scope.location?.authenticated) === 1;
    }
    if (scope.type === 'installer') {
      const locations = scope.locations || [];
      if (!locations.length) return false;
      return locations.every(l => Number(l.authenticated) === 1);
    }
    return true;
  },
};

export default ScopeService;
